import {noop} from '@github-ui/noop'
import {CheckIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {Box} from '@primer/styled-react'
import {clsx} from 'clsx'
import {forwardRef, useCallback, useEffect, useId, useRef} from 'react'

import styles from '../ProjectsSections.module.css'
import styles0 from './FieldWrapper.module.css'

type FieldWrapperProps = {
  name: string
  placeholder: string | React.ReactNode
  value?: React.ReactNode
  input?: React.ReactNode
  inputRef?: React.RefObject<HTMLInputElement | null>
  anchorProps?: React.HTMLAttributes<HTMLElement>
  canUpdate?: boolean
  isStatusField?: boolean
  initialShowInput?: boolean
  showInput: boolean
  setShowInput: React.Dispatch<React.SetStateAction<boolean>>
  onCommit?: () => void
}

type MetadataButtonProps = {
  disabled: boolean
  placeholder?: string
  children: React.ReactNode
  onClick: React.MouseEventHandler<HTMLButtonElement>
  anchorProps?: React.HTMLAttributes<HTMLButtonElement>
  isStatusField?: boolean
}

export function MetadataButton({
  children,
  onClick,
  anchorProps,
  disabled,
  isStatusField = false,
  placeholder,
}: MetadataButtonProps) {
  const sx = disabled
    ? {paddingLeft: '6px', paddingRight: '6px'}
    : {
        '&:hover': {
          backgroundColor: 'actionListItem.default.hoverBg',
        },
      }

  return (
    <Box
      as={disabled ? undefined : 'button'}
      aria-label={disabled ? 'Field is not editable' : placeholder}
      onClick={disabled ? noop : onClick}
      sx={{
        py: isStatusField ? 1 : 'var(--control-medium-paddingBlock)',

        // including just in case the py variable is decreased. This will keep the click target reasonable
        minHeight: isStatusField ? 'var(--control-small-size)' : 'var(--control-xsmall-size)',

        ...sx,
      }}
      className={styles0.metadataFieldButton}
      {...anchorProps}
    >
      <div className={styles0.fieldContentContainer}>{children}</div>
    </Box>
  )
}

export const FieldWrapper = forwardRef<HTMLLIElement, FieldWrapperProps>(function FieldWrapper(
  {
    name,
    placeholder,
    value,
    input,
    inputRef,
    anchorProps,
    showInput,
    setShowInput,
    canUpdate = true,
    isStatusField = false,
    onCommit = noop,
  },
  ref,
) {
  const localRef = useRef(null)
  const labelId = useId()
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (!anchorProps && canUpdate) {
        setShowInput(true)
      } else if (anchorProps?.onClick) {
        anchorProps.onClick(e)
      }
    },
    [anchorProps, canUpdate, setShowInput],
  )

  useEffect(() => {
    if (showInput === true && inputRef && inputRef.current) {
      inputRef.current.focus()
    }
  }, [inputRef, showInput])

  const nameClasses = clsx(styles.ListFieldWrapperText, isStatusField && styles.FieldBasisAuto)

  return (
    <span ref={ref || localRef} className={styles.ListFieldWrapper}>
      {showInput ? (
        <label className={styles.FieldWrapperLabel}>
          <p className={nameClasses}>{name}</p>
          <span className={styles.FieldWrapperInputContainer}>
            {input}
            <IconButton
              aria-label="Update"
              data-testid="commit"
              size="small"
              icon={CheckIcon}
              onClick={() => {
                onCommit()
                setShowInput(false)
              }}
              disabled={!canUpdate}
            />
          </span>
        </label>
      ) : (
        <>
          <p id={labelId} className={nameClasses}>
            {name}
          </p>
          <MetadataButton
            isStatusField={isStatusField}
            anchorProps={{...anchorProps, 'aria-describedby': labelId}}
            onClick={handleClick}
            placeholder={typeof placeholder == 'string' ? placeholder : undefined}
            disabled={!canUpdate}
          >
            {value ? (
              <span className={styles0.fieldValueText}>{value}</span>
            ) : (
              <span className={styles0.fieldPlaceholderText}>{placeholder}</span>
            )}
          </MetadataButton>
        </>
      )}
    </span>
  )
})

try{ MetadataButton.displayName ||= 'MetadataButton' } catch {}
try{ FieldWrapper.displayName ||= 'FieldWrapper' } catch {}